import { useMemo, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { queue } from "@/components/Toast";
import { cn } from "@/utils/helpers";
import type { CalendarPlanSlot } from "@/types/contentCalendar.type";
import PlatformGlyph, { hasGlyph } from "../../components/PlatformGlyph";
import { buildPillarHueMap } from "../../strategy/components/strategyMeta";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { isReminderCooldown, useRemindClient } from "./useClientRoundActions";

/**
 * Client proposals (Figma 08, 5 Client review): every post of the month that
 * went to the client, or is ready to, in four groups (Came back, Ready to send,
 * With the client, Approved). Each row says the post and what was sent, which
 * round and when, the one next step, and the reply-by day with its latest news.
 */

type Group = "back" | "ready" | "with" | "approved";

const DAY = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", timeZone: "UTC" });
const WEEKDAY = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
/** "Fri 4 Dec" (a planned day: read in UTC). */
const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).replace(",", "");

/** "today, 16:20", "Sat 21 Nov, 09:00": a moment, read where the viewer is. */
function moment(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  if (date.toDateString() === now.toDateString()) return `today, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `yesterday, ${time}`;
  return `${date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "")}, ${time}`;
}

function daysLate(answerBy: string): number {
  return Math.max(1, Math.floor((Date.now() - new Date(answerBy).getTime()) / 86_400_000));
}

function pickedIdea(slot: CalendarPlanSlot) {
  return slot.ideas?.find((idea) => idea.status === "ACCEPTED") ?? null;
}

function channelsOf(slot: CalendarPlanSlot): string[] {
  const idea = pickedIdea(slot);
  return [...new Set((idea?.channels?.length ? idea.channels : slot.channels) ?? [])].filter((platform) => hasGlyph(platform));
}

function groupOf(slot: CalendarPlanSlot, brief: BriefRailInfo | undefined): Group | null {
  if (slot.origin === "DIRECT") return null;
  const decision = slot.reviewDecisions?.[0];
  if (slot.status === "IN_CLIENT_REVIEW") return "with";
  if (slot.status === "CLIENT_APPROVED") return slot.deliverable ? "approved" : "back";
  if (slot.status === "SIGNED_OFF") {
    if (decision?.decision === "DECLINED") return "back";
    if (brief && brief.status !== "GENERATING" && brief.status !== "FAILED") return "ready";
  }
  return null;
}

const GROUP_TITLE: Record<Group, (client: string) => string> = {
  back: () => "Came back",
  ready: () => "Ready to send",
  with: (client) => `With ${client}`,
  approved: () => "Approved",
};

export default function ClientProposalsView({
  slots,
  planId,
  year,
  month,
  clientName,
  briefs,
  pillars,
  canEdit,
  onOpenPost,
  onSend,
  onBack,
}: {
  slots: CalendarPlanSlot[];
  planId: string | null;
  year: number;
  /** 1-12. */
  month: number;
  clientName: string;
  briefs: Map<string, BriefRailInfo>;
  pillars?: readonly { id: string; name: string; order?: number }[];
  canEdit: boolean;
  onOpenPost: (slot: CalendarPlanSlot, phase?: "concept" | "publishing") => void;
  /** Opens the send dialog with this post picked. */
  onSend?: (slot: CalendarPlanSlot) => void;
  onBack: () => void;
}) {
  const remind = useRemindClient(planId);
  const monthName = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
  const hues = useMemo(() => buildPillarHueMap(pillars ?? [], slots.map((slot) => slot.pillarId)), [pillars, slots]);
  const ordered = useMemo(() => [...slots].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt) || a.order - b.order), [slots]);
  const grouped = useMemo(() => {
    const out: Record<Group, CalendarPlanSlot[]> = { back: [], ready: [], with: [], approved: [] };
    for (const slot of ordered) {
      const group = groupOf(slot, briefs.get(slot.id));
      if (group) out[group].push(slot);
    }
    return out;
  }, [ordered, briefs]);
  const pipeline = ordered.filter((slot) => slot.origin !== "DIRECT");
  const went = grouped.back.length + grouped.with.length + grouped.approved.length;
  const late = grouped.with.filter((slot) => slot.clientReview?.concept?.late).length;
  const notGone = pipeline.filter((slot) => !groupOf(slot, briefs.get(slot.id)));
  const atIdeas = notGone.find((slot) => slot.status === "IDEATION");

  const remindSlot = (slot: CalendarPlanSlot) => {
    const roundId = slot.clientReview?.concept?.roundId;
    if (!roundId || roundId === "pending") return;
    const input = { slotId: slot.id, kind: "concept" as const, roundId };
    remind.mutate(input, {
      onError: (err) => {
        const cooldown = isReminderCooldown(err);
        if (!cooldown) return;
        queue.add({ title: cooldown, action: { label: "Remind anyway", onPress: () => remind.mutate({ ...input, force: true }) } });
      },
    });
  };

  const summary = (() => {
    const parts: string[] = [];
    if (grouped.back.length) parts.push(`${grouped.back.length} came back`);
    if (grouped.with.length) {
      parts.push(
        `${grouped.with.length} ${grouped.with.length === 1 ? "is" : "are"} with ${clientName}${
          late ? (late === grouped.with.length ? (late === 1 ? ", late" : late === 2 ? ", both late" : ", all late") : `, ${late} late`) : ""
        }`,
      );
    }
    if (grouped.approved.length) parts.push(`${grouped.approved.length} ${grouped.approved.length === 1 ? "is" : "are"} approved`);
    const head = `${monthName} ${year}. ${went} of ${pipeline.length} ${pipeline.length === 1 ? "post" : "posts"} went to ${clientName}`;
    return parts.length ? `${head}: ${parts.join(", ").replace(/, ([^,]*)$/, ", $1")}.` : `${head}.`;
  })();

  const row = (slot: CalendarPlanSlot, group: Group) => {
    const idea = pickedIdea(slot);
    const brief = briefs.get(slot.id);
    const review = slot.clientReview?.concept ?? null;
    const decision = slot.reviewDecisions?.[0];
    const round = Math.max(1, brief?.sentVersion ?? (review ? 1 : 0));
    const hue = slot.pillarId ? hues.get(slot.pillarId) : undefined;
    let next: { label: string; yours: boolean; run?: () => void };
    let replyLine: ReactNode = null;
    let replySub: ReactNode = null;
    if (group === "back") {
      if (slot.status === "CLIENT_APPROVED") {
        next = { label: "Add files", yours: true, run: () => onOpenPost(slot, "publishing") };
        replySub = decision ? `Approved by ${decision.clientName.split(" ")[0]} ${moment(decision.decidedAt)}` : "Approved";
      } else {
        next = { label: `Fix, then send version ${round + 1}`, yours: true, run: () => onOpenPost(slot) };
        replySub = decision ? `${decision.clientName.split(" ")[0]} asked for changes ${moment(decision.decidedAt)}` : "Changes asked";
      }
      replyLine = review?.answerBy ? shortDay(review.answerBy) : null;
    } else if (group === "ready") {
      next = { label: brief?.sentVersion ? `Send version ${(brief.sentVersion ?? 0) + 1}` : "Send", yours: true, run: onSend ? () => onSend(slot) : () => onOpenPost(slot) };
      replyLine = null;
      replySub = <span className="text-co-muted">Not sent yet</span>;
    } else if (group === "with") {
      const reminded = review?.lastReminder;
      next = review?.late
        ? { label: reminded ? "Remind again" : "Remind", yours: false, run: canEdit ? () => remindSlot(slot) : undefined }
        : { label: "Open the post", yours: false, run: () => onOpenPost(slot) };
      replyLine = review?.answerBy ? (
        review.late ? (
          <span className="text-co-alert-ink">
            {shortDay(review.answerBy)} · {daysLate(review.answerBy)} {daysLate(review.answerBy) === 1 ? "day" : "days"} late
          </span>
        ) : (
          shortDay(review.answerBy)
        )
      ) : (
        "No reply-by day"
      );
      replySub = reminded
        ? `Reminded ${moment(reminded.sentAt)}`
        : review?.openedAt
          ? `Opened by ${(review.openedByName ?? clientName).split(" ")[0]} ${moment(review.openedAt)}`
          : "Not opened yet";
    } else {
      next = { label: "Open the post", yours: false, run: () => onOpenPost(slot, "publishing") };
      replyLine = review?.answerBy ? shortDay(review.answerBy) : decision ? shortDay(decision.decidedAt) : null;
      replySub = decision ? `Approved by ${decision.clientName.split(" ")[0]}` : "Approved";
    }
    const channels = channelsOf(slot);
    return (
      <li key={slot.id} className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-3 gap-y-2 border-b border-co-line py-3.5 lg:grid-cols-[84px_minmax(0,1fr)_200px_260px_190px] lg:items-center">
        <span className="flex flex-col">
          <span className="font-section text-[24px] font-bold leading-none text-co-text">{DAY(slot.scheduledAt)}</span>
          <span className="mt-1 text-[12.5px] text-co-muted">{WEEKDAY(slot.scheduledAt)}</span>
        </span>
        <button
          type="button"
          onClick={() => onOpenPost(slot)}
          className="flex min-w-0 flex-col gap-1 border-l-[3px] pl-3 text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
          style={{ borderLeftColor: hue ?? "var(--co-line2)" }}
        >
          <span className="truncate text-[14.5px] font-medium text-co-text hover:underline">{slot.title}</span>
          <span className="flex min-w-0 items-center gap-2 text-[12.5px] text-co-muted">
            <span className="truncate">{idea ? `Idea “${idea.title}” and brief` : "Idea and brief"}</span>
            {channels.map((platform) => (
              <PlatformGlyph key={platform} platform={platform} className="size-3.5 shrink-0 text-co-text2" />
            ))}
          </span>
        </button>
        <span className="col-start-2 flex flex-col text-[12.5px] lg:col-start-auto">
          {group === "ready" ? (
            <span className="text-co-text2">{brief?.sentVersion ? `Round ${round + 1}` : "Round 1"}</span>
          ) : (
            <>
              <span className="text-co-text2">Round {round}</span>
              <span className="text-co-muted">{review?.sentAt ? `Sent ${shortDay(review.sentAt)}` : ""}</span>
            </>
          )}
        </span>
        <span className="col-start-2 lg:col-start-auto">
          <button
            type="button"
            onClick={next.run}
            disabled={!next.run}
            className={cn(
              "inline-flex items-center gap-2 text-[13.5px] font-medium outline-none hover:underline focus-visible:outline-2 focus-visible:outline-co-accent disabled:no-underline",
              next.yours ? "text-co-accent-ink" : "text-co-text2",
            )}
          >
            <span aria-hidden="true" className={cn("size-[7px] rounded-full", next.yours ? "bg-co-accent" : "border border-co-text2")} />
            {next.label}
            {next.run ? <ArrowRight className="size-3.5" aria-hidden="true" /> : null}
          </button>
        </span>
        <span className="col-start-2 flex flex-col text-[12.5px] lg:col-start-auto">
          {replyLine ? <span className="text-co-text2">{replyLine}</span> : null}
          {replySub ? <span className="text-co-text2">{replySub}</span> : null}
        </span>
      </li>
    );
  };

  const section = (group: Group) => {
    const list = grouped[group];
    // An empty group says nothing, except Ready to send, which says what comes next.
    if (!list.length && group !== "ready") return null;
    const sub =
      group === "back"
        ? list.length
          ? "answers to act on"
          : null
        : group === "with" && late
          ? late === list.length
            ? late === 1
              ? "late"
              : late === 2
                ? "both late"
                : "all late"
            : `${late} late`
          : null;
    return (
      <section key={group} aria-labelledby={`proposals-${group}`} className="flex flex-col">
        <h2 id={`proposals-${group}`} className="flex items-baseline gap-2.5 pb-1.5 pt-6">
          <span className="font-section text-[20px] font-bold text-co-text">{GROUP_TITLE[group](clientName)}</span>
          <span className="font-sans text-[13px] font-normal text-co-muted">
            {list.length}
            {sub ? ` · ${sub}` : ""}
          </span>
        </h2>
        {list.length ? (
          <ul className="m-0 list-none p-0">{list.map((slot) => row(slot, group))}</ul>
        ) : group === "ready" ? (
          <p className="pb-2 pl-[96px] text-[13.5px] text-co-text2 max-lg:pl-0">
            Nothing is ready to send.
            {atIdeas ? (
              <>
                {" "}
                {shortDay(atIdeas.scheduledAt)} is at its ideas: pick one, and Creo writes the brief.{" "}
                <button type="button" onClick={() => onOpenPost(atIdeas)} className="font-medium text-co-accent-ink outline-none hover:underline">
                  Open {shortDay(atIdeas.scheduledAt)}
                </button>
              </>
            ) : null}
          </p>
        ) : null}
      </section>
    );
  };

  const empty = went === 0 && grouped.ready.length === 0;

  return (
    <div className="flex flex-col pb-16 pt-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-[50px] font-normal leading-[70px] tracking-normal text-co-text max-sm:text-[38px] max-sm:leading-[1.14]">Client proposals</h1>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-9 items-center rounded-pill bg-co-tonal px-4 text-[13.5px] font-medium text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)] outline-none hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-co-accent"
        >
          Back to {monthName}
        </button>
      </header>
      <p className="mt-2 text-[15px] text-co-text2">{empty ? `${monthName} ${year}. Nothing is with ${clientName}.` : summary}</p>
      {empty ? (
        <div className="mt-10 flex max-w-[520px] flex-col gap-2">
          <p className="text-[15px] font-medium text-co-text">Nothing has gone to {clientName} yet</p>
          <p className="text-[13.5px] leading-[1.5] text-co-text2">
            A post comes here once its idea is picked and Creo has written the brief: ready to send, then with {clientName}, then back with their answer.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-8 hidden grid-cols-[84px_minmax(0,1fr)_200px_260px_190px] gap-x-3 border-b border-co-line pb-2 text-[12.5px] text-co-muted lg:grid">
            <span>Day</span>
            <span>Post and what was sent</span>
            <span>Round and sent</span>
            <span>Next</span>
            <span>Reply by</span>
          </div>
          {(["back", "ready", "with", "approved"] as const).map(section)}
          <p className="pt-4 text-[13.5px] text-co-text2 lg:pl-[96px]">
            That is all {went === 1 ? "one" : went} that went to {clientName}.
            {notGone.length ? ` ${notGone.map((slot) => shortDay(slot.scheduledAt)).slice(0, 3).join(", ")}${notGone.length > 3 ? " and others" : ""} ${notGone.length === 1 ? "has" : "have"} not gone yet.` : ""}
          </p>
        </>
      )}
    </div>
  );
}
